import {
  isRecordShortcutType,
  type RecordShortcut,
  recordShortcutId,
} from "./record-shortcuts";
import { findCatalogShortcut } from "./shortcut-catalog";

/**
 * Raccourcis de l'accueil : une liste ordonnée propre à chaque utilisateur
 * (préférence BO `dashboard.shortcuts`). Un raccourci est une entrée du
 * catalogue (action d'ajout, recherche, navigation), un lien personnalisé vers
 * une page du back-office, ou une fiche épinglée depuis son en-tête.
 */
export const DASHBOARD_SHORTCUTS_PREF_KEY = "dashboard.shortcuts";

export type ShortcutIcon =
  | "bookmark"
  | "search"
  | "plus"
  | "pencil"
  | "upload"
  | "download"
  | "file-text"
  | "file-plus"
  | "trash"
  | "globe"
  | "shopping-cart"
  | "book"
  | "user-plus"
  | "gavel"
  | "layers"
  | "camera"
  | "film"
  | "pen-line"
  | "shield-check"
  | "settings"
  | "link"
  | "palette"
  | "user"
  | "landmark";

/** Les raccourcis de la maquette, proposés tant que l'utilisateur n'a rien réglé. */
export const DEFAULT_SHORTCUT_IDS = [
  "artwork.new",
  "artwork.search",
  "news.new",
  "selection.edit",
  "collector.search",
  "collector.new",
  "exhibition.new",
  "import.artwork",
];

export const MAX_SHORTCUTS = 12;
export const MAX_SHORTCUT_LABEL_LENGTH = 60;

/** Texte ramené à un nom de pastille : espaces normalisés, tronqué. */
export function shortcutLabel(text: string): string {
  const label = text.replace(/\s+/g, " ").trim();
  return label.length > MAX_SHORTCUT_LABEL_LENGTH
    ? `${label.slice(0, MAX_SHORTCUT_LABEL_LENGTH - 1).trimEnd()}…`
    : label;
}

type CustomShortcut = { id: string; custom: { label: string; path: string } };
type PinnedRecordShortcut = { id: string; record: RecordShortcut };

export type StoredShortcut =
  | { id: string }
  | CustomShortcut
  | PinnedRecordShortcut;

export function isCustomShortcut(s: StoredShortcut): s is CustomShortcut {
  return "custom" in s;
}

export function isRecordShortcut(s: StoredShortcut): s is PinnedRecordShortcut {
  return "record" in s;
}

export function parseStoredShortcuts(value: unknown): StoredShortcut[] {
  if (!Array.isArray(value)) {
    return DEFAULT_SHORTCUT_IDS.map((id) => ({ id }));
  }
  const seen = new Set<string>();
  const result: StoredShortcut[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const { id, custom, record } = item as {
      id?: unknown;
      custom?: unknown;
      record?: unknown;
    };
    if (typeof id !== "string" || seen.has(id)) continue;
    if (record && typeof record === "object") {
      const parsed = parseRecord(record);
      if (parsed && id === recordShortcutId(parsed.type, parsed.id)) {
        seen.add(id);
        result.push({ id, record: parsed });
      }
      continue;
    }
    if (custom && typeof custom === "object") {
      const { label, path } = custom as { label?: unknown; path?: unknown };
      if (typeof label === "string" && typeof path === "string") {
        seen.add(id);
        result.push({ id, custom: { label, path } });
      }
      continue;
    }
    if (findCatalogShortcut(id)) {
      seen.add(id);
      result.push({ id });
    }
  }
  return result;
}

function parseRecord(value: object): RecordShortcut | null {
  const { type, id, label } = value as Record<string, unknown>;
  if (!isRecordShortcutType(type)) return null;
  if (typeof id !== "string" || !id || typeof label !== "string") return null;
  return { type, id, label };
}

/**
 * Normalise ce que l'utilisateur colle dans « lien personnalisé » : une URL
 * complète du back-office ou un chemin. Seules les pages internes sont
 * acceptées (pas de lien vers un site tiers).
 */
export function normalizeInternalPath(
  input: string,
  origin: string = window.location.origin,
): string | null {
  const value = input.trim();
  if (!value) return null;
  try {
    const url = new URL(value, origin);
    if (url.origin !== origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function newCustomShortcutId(): string {
  return `custom:${crypto.randomUUID()}`;
}

/**
 * Applique le nouvel ordre des raccourcis visibles. Ceux masqués pour cette
 * instance (e-shop absent…) ne sont pas affichés mais restent enregistrés, à
 * la fin de la liste.
 */
export function reorderShortcuts(
  stored: StoredShortcut[],
  visibleIdsInOrder: string[],
): StoredShortcut[] {
  const byId = new Map(stored.map((s) => [s.id, s]));
  const reordered = visibleIdsInOrder.flatMap((id) => byId.get(id) ?? []);
  const visible = new Set(visibleIdsInOrder);
  return [...reordered, ...stored.filter((s) => !visible.has(s.id))];
}

export type RecordToggleOutcome = "added" | "removed" | "full";

/** Épingle la fiche (à la fin de la liste) ou la retire si elle l'était déjà. */
export function toggleRecordShortcut(
  stored: StoredShortcut[],
  record: RecordShortcut,
): { next: StoredShortcut[]; outcome: RecordToggleOutcome } {
  const id = recordShortcutId(record.type, record.id);
  if (stored.some((s) => s.id === id)) {
    return { next: stored.filter((s) => s.id !== id), outcome: "removed" };
  }
  if (stored.length >= MAX_SHORTCUTS) return { next: stored, outcome: "full" };
  return { next: [...stored, { id, record }], outcome: "added" };
}
